import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getAuthToken = () => {
    const adminData = sessionStorage.getItem('adminkhadamat');
    if (adminData) {
        try {
            const parsed = JSON.parse(adminData);
            if (parsed.data?.token?.access_token) {
              const token = String(parsed.data.token.access_token);
              return token;
            }
            if (parsed.data?.access_token) return parsed.data.access_token;
            if (parsed.data?.token) return parsed.data.token;
            if (parsed.token) return parsed.token;
            if (parsed.result?.token) return parsed.result.token;
            if (parsed.user?.token) return parsed.user.token;
            return null;
        } catch {
            return null;
        }
    }
    return null;
};

// ------------------------- Countries -------------------------

// Fetch all countries
export const fetchCountries = createAsyncThunk(
    'locations/fetchCountries',
    async (_, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/countries`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });
            // The response format according to requirements: { "result": [...] } or standard
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في جلب الدول');
        }
    }
);

// Add Country
export const addCountry = createAsyncThunk(
    'locations/addCountry',
    async (formData, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.post(`${BASE_URL}/add/country`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في إضافة الدولة');
        }
    }
);

// Get Country Details
export const fetchCountryDetails = createAsyncThunk(
    'locations/fetchCountryDetails',
    async (id, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/locations/country/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في جلب تفاصيل الدولة');
        }
    }
);

// Update Country
export const updateCountry = createAsyncThunk(
    'locations/updateCountry',
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.post(`${BASE_URL}/locations/country/${id}`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في تحديث الدولة');
        }
    }
);

// ------------------------- Governments -------------------------

// Fetch Governments by Country
export const fetchGovernments = createAsyncThunk(
    'locations/fetchGovernments',
    async (countryId, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/country_governs/${countryId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في جلب المحافظات');
        }
    }
);

// Add Government
export const addGovernment = createAsyncThunk(
    'locations/addGovernment',
    async (formData, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.post(`${BASE_URL}/add/government`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في إضافة المحافظة');
        }
    }
);

// Get Government Details
export const fetchGovernmentDetails = createAsyncThunk(
    'locations/fetchGovernmentDetails',
    async (id, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/locations/govern/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في جلب تفاصيل المحافظة');
        }
    }
);

// Update Government
export const updateGovernment = createAsyncThunk(
    'locations/updateGovernment',
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.post(`${BASE_URL}/locations/govern/${id}`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في تحديث المحافظة');
        }
    }
);

// ------------------------- Centers -------------------------

// Fetch Centers by Government
export const fetchCenters = createAsyncThunk(
    'locations/fetchCenters',
    async (governmentId, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/govern_centers/${governmentId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في جلب المراكز');
        }
    }
);

// Add Center
export const addCenter = createAsyncThunk(
    'locations/addCenter',
    async (formData, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.post(`${BASE_URL}/add/center`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في إضافة المركز');
        }
    }
);

// Get Center Details
export const fetchCenterDetails = createAsyncThunk(
    'locations/fetchCenterDetails',
    async (id, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/locations/center/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في جلب تفاصيل المركز');
        }
    }
);

// Update Center
export const updateCenter = createAsyncThunk(
    'locations/updateCenter',
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.post(`${BASE_URL}/locations/center/${id}`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في تحديث المركز');
        }
    }
);

// Slice
const locationsSlice = createSlice({
    name: 'locations',
    initialState: {
        countries: [],
        governments: [],
        centers: [],
        countryDetails: null,
        governmentDetails: null,
        centerDetails: null,
        loading: false,
        updating: false,
        error: null,
        success: null,
    },
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccess: (state) => {
            state.success = null;
        },
        clearLocationDetails: (state) => {
            state.countryDetails = null;
            state.governmentDetails = null;
            state.centerDetails = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // fetchCountries
            .addCase(fetchCountries.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCountries.fulfilled, (state, action) => {
                state.loading = false;
                state.countries = action.payload.result || action.payload.data || [];
            })
            .addCase(fetchCountries.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // fetchCountryDetails
            .addCase(fetchCountryDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCountryDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.countryDetails = action.payload.data || action.payload.result || action.payload;
            })
            .addCase(fetchCountryDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            
            // fetchGovernments
            .addCase(fetchGovernments.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchGovernments.fulfilled, (state, action) => {
                state.loading = false;
                state.governments = action.payload.result || action.payload.data || [];
            })
            .addCase(fetchGovernments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // fetchGovernmentDetails
            .addCase(fetchGovernmentDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchGovernmentDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.governmentDetails = action.payload.data || action.payload.result || action.payload;
            })
            .addCase(fetchGovernmentDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // fetchCenters
            .addCase(fetchCenters.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCenters.fulfilled, (state, action) => {
                state.loading = false;
                state.centers = action.payload.result || action.payload.data || [];
            })
            .addCase(fetchCenters.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // fetchCenterDetails
            .addCase(fetchCenterDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCenterDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.centerDetails = action.payload.data || action.payload.result || action.payload;
            })
            .addCase(fetchCenterDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Unified Updates
            .addMatcher(
                (action) => [
                    addCountry.pending.type, updateCountry.pending.type,
                    addGovernment.pending.type, updateGovernment.pending.type,
                    addCenter.pending.type, updateCenter.pending.type
                ].includes(action.type),
                (state) => {
                    state.updating = true;
                    state.error = null;
                }
            )
            .addMatcher(
                (action) => [
                    addCountry.fulfilled.type, updateCountry.fulfilled.type,
                    addGovernment.fulfilled.type, updateGovernment.fulfilled.type,
                    addCenter.fulfilled.type, updateCenter.fulfilled.type
                ].includes(action.type),
                (state, action) => {
                    state.updating = false;
                    state.success = action.payload?.message || 'تمت العملية بنجاح';
                }
            )
            .addMatcher(
                (action) => [
                    addCountry.rejected.type, updateCountry.rejected.type,
                    addGovernment.rejected.type, updateGovernment.rejected.type,
                    addCenter.rejected.type, updateCenter.rejected.type
                ].includes(action.type),
                (state, action) => {
                    state.updating = false;
                    state.error = action.payload;
                }
            );
    },
});

export const { clearError, clearSuccess, clearLocationDetails } = locationsSlice.actions;
export default locationsSlice.reducer;
